import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Button, Card } from '@evw/ui';
import { useMemo, useState, type FormEvent } from 'react';
import { createPaymentIntent, type PaymentOption, type PreparedPayment } from './api';

function money(amountMinor: number, currency: string) {
  return (amountMinor / 100).toLocaleString(undefined, { style: 'currency', currency });
}

function requestId(bookingId: string, option: string) {
  const key = `evw-payment-request:${bookingId}:${option}`;
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const created = crypto.randomUUID();
  sessionStorage.setItem(key, created);
  return created;
}

function StripeForm({ onProcessing }: { onProcessing: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError('');
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/manage` },
      redirect: 'if_required'
    });
    if (result.error) setError(result.error.message ?? 'Payment could not be completed');
    else onProcessing();
    setSubmitting(false);
  }

  return (
    <form className="stack payment-form" onSubmit={(event) => void submit(event)}>
      <PaymentElement options={{ layout: 'tabs' }} />
      {error ? <p className="error">{error}</p> : null}
      <Button disabled={!stripe || submitting}>
        {submitting ? 'Submitting…' : 'Submit payment'}
      </Button>
      <small>Confirmation is based on Stripe's verified server notification, not this page.</small>
    </form>
  );
}

export function PaymentPanel({
  bookingId,
  token,
  options,
  onProcessing
}: {
  bookingId: string;
  token: string;
  options: PaymentOption[];
  onProcessing: () => void;
}) {
  const [prepared, setPrepared] = useState<PreparedPayment | null>(null);
  const [error, setError] = useState('');
  const stripePromise = useMemo(
    () =>
      prepared
        ? loadStripe(prepared.publishableKey, { stripeAccount: prepared.connectedAccountId })
        : null,
    [prepared]
  );

  async function select(option: PaymentOption) {
    setError('');
    try {
      setPrepared(
        await createPaymentIntent(token, option.option, requestId(bookingId, option.option))
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Payment setup failed');
    }
  }

  if (options.length === 0)
    return (
      <div className="payment-placeholder">
        <strong>Payment is not currently available</strong>
        <p>Your booking remains unconfirmed. Please contact the business for assistance.</p>
      </div>
    );

  return (
    <Card>
      <h2>Payment</h2>
      <p>Select one of the available payment options. Amounts are calculated by the server.</p>
      <div className="payment-options">
        {options.map((option) => (
          <button
            type="button"
            className={
              prepared?.option === option.option ? 'payment-option selected' : 'payment-option'
            }
            key={option.option}
            onClick={() => void select(option)}
          >
            <strong>{option.label}</strong>
            <span>{money(option.amountMinor, option.currency)}</span>
          </button>
        ))}
      </div>
      {error ? <p className="error">{error}</p> : null}
      {prepared && stripePromise ? (
        <Elements
          stripe={stripePromise}
          options={{ clientSecret: prepared.clientSecret, appearance: { theme: 'stripe' } }}
          key={prepared.clientSecret}
        >
          <StripeForm onProcessing={onProcessing} />
        </Elements>
      ) : null}
    </Card>
  );
}
